//@ts-nocheck
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFieldNote,
  deleteFieldNote,
  updateFieldNote,
} from '@/api/fieldNotes';
import { useAuth } from '@/providers/AuthProvider';
import { handleAPIError } from '@/utils/errorHandler';
import { getFieldNotesLayer } from '@/api/map-layers';
import { useMapStore } from '@/stores/mapStore';
import { SystemLayerKey } from 'node_modules/@packages/database/src/types/layers';

const getFieldNoteLayerId = () => {
  const layers = useMapStore.getState().layers;
  return layers.find((l) => l.key === SystemLayerKey.FIELD_NOTE)?.id;
};

export const useFieldNotes = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['fieldNotes', orgId],
    queryFn: () => getFieldNotesLayer(),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useUpdateFieldNote = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (note: any) => updateFieldNote(note),
    onSuccess: () => {
      const targetLayerId = getFieldNoteLayerId();

      queryClient.invalidateQueries({
        queryKey: ['features', orgId, targetLayerId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateFieldNote: mutation.mutate,
    updateFieldNoteAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useCreateFieldNote = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (newNote) => createFieldNote(newNote),
    onSuccess: (data, variables) => {
      const targetLayerId = getFieldNoteLayerId();

      queryClient.invalidateQueries({
        queryKey: ['features', orgId, targetLayerId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createFieldNote: mutation.mutate,
    createFieldNoteAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useDeleteFieldNote = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (noteId: string) => deleteFieldNote(noteId),
    onSuccess: () => {
      const targetLayerId = getFieldNoteLayerId();

      queryClient.invalidateQueries({
        queryKey: ['features', orgId, targetLayerId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    deleteFieldNote: mutation.mutate,
    deleteFieldNoteAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
